import React, { useState } from "react";
import { useStore } from "../store";
import { EIGHT_D_STEPS, eightDStatusStyle } from "../data/eightDData";
import { useEightDs } from "../api/sitroom";
import { useT } from "../lib/i18n";

const filterPills = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "in-progress", label: "In Progress" },
  { id: "closed", label: "Closed" },
  { id: "pending-approval", label: "Pending Approval" },
];

const StepIndicator = ({ progress }) => (
  <div className="flex items-center gap-1">
    {EIGHT_D_STEPS.map((s, i) => {
      const done = i < progress;
      const current = i === progress;
      return (
        <div
          key={s.code}
          title={`${s.code} — ${s.label}`}
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: done ? "#27ae60" : current ? "#185FA5" : "transparent",
            border: `1.5px solid ${done ? "#27ae60" : current ? "#185FA5" : "rgba(255,255,255,0.15)"}`,
            boxShadow: current ? "0 0 6px rgba(24,95,165,0.7)" : "none",
          }}
        />
      );
    })}
    <span
      className="ml-1.5"
      style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }}
    >
      {EIGHT_D_STEPS[Math.min(progress, 8)].code}
    </span>
  </div>
);

export default function EightD() {
  const openPanel = useStore((s) => s.openEightDPanel);
  const openPanelInc = useStore((s) => s.openPanel);
  const openNew = useStore((s) => s.openNewEightDModal);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { data: eightDReports = [] } = useEightDs();
  const { t } = useT();

  const filtered = eightDReports.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search && !`${r.id} ${r.title} ${r.sourceIncident}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="eightd-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 34px)",
              color: "var(--text)",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            {t("eightd.title")}
          </h1>
          <div
            className="mt-1.5 font-dm-mono"
            style={{ fontSize: 11, color: "#c5cad8", letterSpacing: "0.4px" }}
          >
            {t("eightd.subtitle")}
          </div>
        </div>
        <button
          onClick={() => openNew()}
          className="px-3 py-2 rounded-md"
          style={{
            background: "#185FA5",
            color: "#fff",
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "0.4px",
          }}
          data-testid="new-8d-btn"
        >
          ＋ New 8D
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {filterPills.map((p) => {
            const active = filter === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setFilter(p.id)}
                className="px-3 py-1.5 rounded-full"
                style={{
                  background: active ? "#185FA5" : "transparent",
                  border: `1px solid ${active ? "#185FA5" : "rgba(255,255,255,0.1)"}`,
                  color: active ? "#fff" : "#9aa1b3",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.4px",
                }}
                data-testid={`8d-filter-${p.id}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="flex-1" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search 8D…"
          className="rounded-md px-3 py-2"
          style={{
            width: 200,
            maxWidth: "100%",
            background: "var(--bg2)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "var(--text)",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
          }}
        />
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: 900 }}>
          <div
            className="grid items-center gap-3 px-4 py-2"
            style={{
              gridTemplateColumns: "130px 1fr 130px 140px 130px 180px 110px",
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              letterSpacing: "1.4px",
              color: "#9198b0",
              textTransform: "uppercase",
            }}
          >
            <div>8D Number</div>
            <div>Working Title</div>
            <div>Source</div>
            <div>Customer</div>
            <div>Status</div>
            <div>Progress</div>
            <div>Updated</div>
          </div>
          {filtered.map((r) => {
            const st = eightDStatusStyle(r.status);
            return (
              <div
                key={r.id}
                onClick={() => openPanel(r.id)}
                className="row-hover grid items-center gap-3 px-4 py-3 cursor-pointer"
                style={{
                  gridTemplateColumns: "130px 1fr 130px 140px 130px 180px 110px",
                  background: "var(--bg2)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 6,
                  marginBottom: 6,
                }}
                data-testid={`8d-row-${r.id}`}
              >
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9ec3ec" }}>
                  {r.id}
                </span>
                <span style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text)" }}>
                  {r.title}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); openPanelInc(r.sourceIncident); }}
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 10,
                    color: "#9ec3ec",
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                    textAlign: "left",
                  }}
                >
                  {r.sourceIncident}
                </button>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cfd1d8" }}>
                  {r.customer}
                </span>
                <span
                  style={{
                    background: st.bg,
                    border: `1px solid ${st.border}`,
                    color: st.color,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    padding: "2px 7px",
                    borderRadius: 3,
                    letterSpacing: "1px",
                    justifySelf: "start",
                  }}
                >
                  {st.label}
                </span>
                <StepIndicator progress={r.progress} />
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }}>
                  {r.lastUpdated}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
